import { CompositeFilterDescriptor, FilterDescriptor, State, toODataString } from "@progress/kendo-data-query";
import { getServicesUri } from "../../api/uriHelper";
import { ICustomFilterExpression } from "./ODataReader";

export function getUrl(
    url: string,
    dataState: State,
    additionalQueries: string[] | undefined,
    customFilterExpressions: Array<ICustomFilterExpression> | undefined,
    additionalFilters: CompositeFilterDescriptor | undefined,
    suppressSkipAndTake: boolean
): string {
    if (!dataState || !dataState.filter) throw new Error();

    // so - the Kendo 'toODataString' has lots of limitations
    // to avoid generating malformed requests based on incomplete query structures, we remove any empty expression groups
    // this is to work around a kendo bug; it is clear they have not performed rudimentary testing
    // there are also many expressions they cannot/do not generate properly, so we need to patch in 'custom expressions'
    // yes, it is very very hacky but has been done out of necessity until we can get kendo to help us with this
    // unfortunately their documentation is severely lacking
    // TODO work with Kendo on a better solution here

    const customExpressions = customFilterExpressions ? getCustomExpressions(dataState.filter!, customFilterExpressions) : [];

    // filter the filters, keeping only what we want to use, and inject placeholders for custom expressions
    const filter = getFilter(dataState.filter!, customExpressions, additionalFilters);

    // generate OData query string (using Kendo's standard logic)
    // - clone the data state so we can manipulate it without mutating the parameter passed in
    // - we may need to clear the skip/take parameters
    const dataStateClone = { ...dataState };
    dataStateClone.filter = filter;
    if (suppressSkipAndTake) {
        dataStateClone.skip = undefined;
        dataStateClone.take = undefined;
    }
    let query = toODataString(dataStateClone);

    // Kendo has a bug with toODataString, it partially encodes the string, this is a problem when using encodeURI as we can end with an incorrect query
    // Specifically with spaces ' ' that are double encoded and sent as %2520 instead of %20.
    // https://github.com/telerik/kendo-react/issues/3025
    // Note: There is an issue with this fix that it doesn't support &, we unfortunatly can't selectivly encode/decode & easily so that is reliant on the Kendo fix.
    query = decodeURIComponent(query);

    // inject custom expressions
    // - find and replace expression placeholders
    query = replaceCustomExpressionPlaceholders(query, customExpressions);

    // add any additional query options
    // - this can be used to expand navigation properties
    if (additionalQueries) {
        for (const q of additionalQueries!) query = query + "&" + q;
    }

    // add 'count=true' as this is needed for the paging control
    query = query + "&$count=true";

    // return the final query
    query = getServicesUri() + "/" + url + "?" + encodeURI(query);
    return query;

    function getCustomExpressions(
        compositeFilterDescriptor: CompositeFilterDescriptor,
        customFilterExpressions: Array<ICustomFilterExpression>
    ): { key: string; name: string; expression: string | undefined }[] {
        // generates OData expressions for any supplied custom filters
        // the key identifies the custom expression instance

        if (!compositeFilterDescriptor) throw new Error("compositeFilterDescriptor is required!");
        if (!customFilterExpressions) throw new Error("customFilterExpressions is required!");
        if (customFilterExpressions.find((e) => !e.name.startsWith("@"))) throw new Error("customFilterExpressions must all use a name that starts with '@'!");
        if (customFilterExpressions.find((e) => customFilterExpressions.filter((e2) => e.name === e2.name).length > 1)) throw new Error("customFilterExpression names must be unique!");

        let result: { key: string; name: string; expression: string | undefined }[] = [];

        // walk the supplied filter hierarchy and detect where we need to build custom expressions

        for (const f of compositeFilterDescriptor.filters) {
            if (isFilterDescriptor(f)) {
                // process filters
                // - ignore expressions that won't require a custom expression
                // - only add unique expressions
                const fd = f as FilterDescriptor;
                const customFilterExpression = customFilterExpressions.find((cfe) => cfe.name === fd.field);
                if (!customFilterExpression) continue;
                const expression = customFilterExpression.expression(fd.value, fd.operator);
                const key = getCustomExpressionKey(fd);
                if (!result.find((e) => e.key === key)) result.push({ key: key, name: customFilterExpression.name, expression: expression });
            }

            // process composite filters (filter groups)
            // - recursive
            // - ignore empty groups
            // - only add unique expressions
            if (isCompositeFilterDescriptor(f)) {
                const cf = f as CompositeFilterDescriptor;
                if (getNumberOfFiltersUnderCompositeFilterDescriptor(cf) === 0) continue;
                const childResult = getCustomExpressions(cf, customFilterExpressions);
                for (const r of childResult) {
                    if (result.find((e) => e.key === r.key)) continue;
                    result.push(r);
                }
            }
        }

        return result;
    }

    function getFilter(
        compositeFilterDescriptor: CompositeFilterDescriptor,
        customExpressions: { key: string; name: string; expression: string | undefined }[],
        additionalFilters: CompositeFilterDescriptor | undefined
    ): CompositeFilterDescriptor {
        if (!compositeFilterDescriptor) throw new Error("compositeFilterDescriptor is required!");
        if (!customExpressions) throw new Error("customExpressions is required!");

        const result: CompositeFilterDescriptor = { logic: compositeFilterDescriptor.logic, filters: [] };

        // walk the supplied filter hierarchy and build an alternate hierarchy

        for (const f of compositeFilterDescriptor.filters) {
            if (isFilterDescriptor(f)) {
                // process filters
                // - inject placeholder expressions for custom expressions
                // - ignore custom expressions that return 'undefined'
                // - add normal field expressions
                const fd = f as FilterDescriptor;
                const fieldName = fd.field as string;
                if (!fieldName) throw new Error("The field name must contain a value!");
                // custom expression
                const ce = customExpressions.find((ce2) => ce2.key === getCustomExpressionKey(fd));
                if (fieldName.startsWith("@") && !ce) throw new Error(`A custom expression is missing for field '${fieldName}'!`);
                if (ce) {
                    if (!ce.expression) continue;
                    result.filters.push({ field: fd.field, operator: "eq", value: ce.key } as FilterDescriptor);
                    continue;
                }
                // add it!
                result.filters.push(f);
            }

            // process composite filters (filter groups)
            // - recursive
            // - ignore empty groups
            if (isCompositeFilterDescriptor(f)) {
                const cf = f as CompositeFilterDescriptor;
                const children = getFilter(cf, customExpressions, undefined);
                if (getNumberOfFiltersUnderCompositeFilterDescriptor(children) === 0) continue;
                result.filters.push(children);
            }
        }

        // If we have additional filters, apply at the top level.
        if (additionalFilters) {
            if (result.filters.length === 0) return additionalFilters;
            else return { logic: "and", filters: [result, additionalFilters] };
        }

        return result;
    }

    function getCustomExpressionKey(filterDescriptor: FilterDescriptor): string {
        // generates a unique key for the supplied expression
        // - one considered alternative to the approach used below is to generate an alphanumeric sequence from the raw hex data, which would avoid being escaped
        return JSON.stringify({ name: filterDescriptor.field!, operator: filterDescriptor.operator, value: filterDescriptor.value });
    }

    function replaceCustomExpressionPlaceholders(query: string, customExpressions: { key: string; name: string; expression: string | undefined }[]): string {
        // inject custom expressions
        // - find and replace expressions based on the the keys embedded in the placeholder expressions

        /*
        // for debugging only!
        console.log("BEFORE INJECTION");
        console.log(query);
        console.log(customExpressions);
        */

        let result = query;
        for (const ce of customExpressions) {
            // don't inject custom expressions where there is no expression to inject
            if (!ce.expression) continue;

            // replace all instances of the given expression
            // - note that kendo uses 'encodeURIComponent' to format the key
            const placeholderExpression = ce.name + " eq '" + encodeURIComponent(ce.key) + "'";
            const newQuery = result.replaceAll(placeholderExpression, ce.expression);
            if (newQuery === query) throw new Error(`The custom expression for '${ce.name}' was not replaced!`);
            result = newQuery;
        }

        /*
        // for debugging only!
        console.log("AFTER INJECTION");
        console.log(result);
        */

        return result;
    }
}

function getNumberOfFiltersUnderCompositeFilterDescriptor(compositeFilterDescriptor: CompositeFilterDescriptor): number {
    if (!compositeFilterDescriptor.filters || compositeFilterDescriptor.filters.length === 0) return 0;

    let result = 0;
    for (let i = 0; i < compositeFilterDescriptor.filters.length; i++) {
        const f = compositeFilterDescriptor.filters[i];

        // count FilterDescriptors
        if (isFilterDescriptor(f)) result++;

        // count FilterDescriptors under CompositeFilterDescriptors
        if (isCompositeFilterDescriptor(f)) {
            const cf = f as CompositeFilterDescriptor;
            result += getNumberOfFiltersUnderCompositeFilterDescriptor(cf);
        }
    }
    return result;
}

function isFilterDescriptor(filter: CompositeFilterDescriptor | FilterDescriptor | undefined): boolean {
    // the FilterDescriptor contains a mandatory 'Operator' field that we check for
    if (!filter) return false;
    const f: any = filter!;
    return !!f.operator;
}

function isCompositeFilterDescriptor(filter: CompositeFilterDescriptor | FilterDescriptor | undefined): boolean {
    // the CompositeFilterDescriptor contains a mandatory 'Logic' field that we check for
    if (!filter) return false;
    const f: any = filter!;
    return !!f.logic;
}

const maximumFilters = 15;
const maximumValueLength = 100;

export function getDataStateFilterValidationMessages(compositeFilterDescriptor: CompositeFilterDescriptor | undefined): string | undefined {
    if (!compositeFilterDescriptor) return undefined;

    if (filterExpressionContainsLongValues(compositeFilterDescriptor!)) {
        return `The filter criteria contains values that exceed ${maximumValueLength} characters; please adjust your filter criteria and try again.`;
    }

    if (filterExpressionContainsTooManyNodes(compositeFilterDescriptor!)) {
        return "Your filter criteria is too complex; please adjust your filter criteria and try again.";
    }

    return undefined;
}

function filterExpressionContainsLongValues(compositeFilterDescriptor: CompositeFilterDescriptor): boolean {
    // verifies the length of values supplied aren't too long

    // - verify filters
    const filterDescriptorResults = compositeFilterDescriptor.filters.filter((f: any) => f.value && f.value.toString().length > maximumValueLength);
    if (filterDescriptorResults.length > 0) return true;

    // - verify composite filters
    if (!compositeFilterDescriptor.filters) return false;
    const compositeFilterDescriptorResults = compositeFilterDescriptor.filters.filter((f: any) => f.filters && filterExpressionContainsLongValues(f));
    if (compositeFilterDescriptorResults.length > 0) return true;

    // - all good!
    return false;
}

function filterExpressionContainsTooManyNodes(compositeFilterDescriptor: CompositeFilterDescriptor): boolean {
    // verifies we don't have too many filters defined
    const numberOfFilters = getNumberOfFiltersUnderCompositeFilterDescriptor(compositeFilterDescriptor);
    return numberOfFilters > maximumFilters;
}
